import { useContext } from 'react';
import { useContextSelector } from 'use-context-selector';

import { formatObjectToReadableText } from 'helpers/formatObjectToReadableText';
import { useAtsVacancyFullQuery } from 'hooks/autocomplete/useAtsVacancyQuery';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useGetJobImprovedQuery } from 'shared/legacy/hooks/useGetJobImprovedQuery';
import { useHasValidAtsConnectionQuery } from 'shared/legacy/hooks/useHasValidAtsConnectionQuery';
import { sanitizeEmailHtml } from 'utils/sanitizeEmailHtml';
import { htmlToPlainText } from 'utils/shared/htmlToPlainText';
import { EnrichVacancyModalContext } from '../../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalType } from '../../EnrichVacancyModalContext/EnrichVacancyModalContext.types';
import { useStyles } from './JobDescription.styles';

export const useJobDescription = () => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);

  const { data: hasValidAts } = useHasValidAtsConnectionQuery();

  const vacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.vacancy);
  const modalType = useContextSelector(EnrichVacancyModalContext, (s) => s.modalType);
  const { data: jobImproved, isFetching: isLoadingJobImproved } = useGetJobImprovedQuery(vacancy?.db_id);
  const { data: atsVacancy, isFetching: isLoadingAtsVacancy } = useAtsVacancyFullQuery({
    id: vacancy?.job_ats_id || '',
    enabled: Boolean(vacancy?.job_ats_id),
  });

  if (modalType === EnrichVacancyModalType.Indeed && vacancy?.type === 'INDEED') {
    const indeedDescription = sanitizeEmailHtml(vacancy.originalIndeedJob?.description || '');
    const indeedTextToCopy = htmlToPlainText(indeedDescription || '', { preserveTextLineBreak: true });

    return {
      textToCopy: indeedTextToCopy,
      copySuccessMessage: dictionary.theIndeedContentHasBeenCopied,
      description: indeedDescription ? (
        <div css={classes.vacancyDescription} dangerouslySetInnerHTML={{ __html: indeedDescription }} />
      ) : (
        ''
      ),
      isLoading: false,
      indeedJobLink: vacancy.originalIndeedJob?.key
        ? `https://employers.indeed.com/jobs/view?employerJobId=${encodeURI(vacancy.originalIndeedJob.key)}`
        : undefined,
    };
  }

  if (modalType === EnrichVacancyModalType.Ats && !hasValidAts) {
    return {
      textToCopy: '',
      copySuccessMessage: '',
      description: '',
      isLoading: false,
      hasInvalidAts: true,
    };
  }

  const atsDescription =
    (atsVacancy?.ai_details
      ? formatObjectToReadableText({
          ...atsVacancy.ai_details,
          complementary_description: atsVacancy.description
            ? htmlToPlainText(atsVacancy.description, { preserveTextLineBreak: true })
            : null,
        })
      : '') ||
    atsVacancy?.description ||
    jobImproved?.origin_text ||
    '';

  return {
    textToCopy: atsDescription,
    copySuccessMessage: dictionary.theAtsContentHasBeenCopied,
    description: atsDescription,
    isLoading: isLoadingAtsVacancy || isLoadingJobImproved,
  };
};
